import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { isShallowEqual } from "~/tools/is-shallow-equal";

import { calculateAutoHeight } from "./_utils/calculate-auto-height";
import type { Monaco } from "./_monaco";

export interface EditorHeightSpec {
	minLines?: number;
	maxLines?: number;
}

interface EditorAutoHeightProps {
	monaco: null | typeof Monaco;
	editor: null | Monaco.editor.IStandaloneCodeEditor;
	borderWidth: number;
	children: React.ReactNode;
	height: undefined | "auto" | EditorHeightSpec;
}

export const EditorAutoHeight: React.FC<EditorAutoHeightProps> = ({
	monaco,
	editor,
	borderWidth,
	height,
	children,
}: EditorAutoHeightProps) => {
	const stableHeight = useComputed(() => height, [height], { equals: isShallowEqual });

	React.useEffect(() => {
		if (stableHeight != null && editor != null && monaco != null) {
			const updateHeight = () => {
				const calculatedHeight = calculateAutoHeight({ editor, monaco, borderWidth, height: stableHeight });

				editor.layout({ width: editor.getLayoutInfo().width, height: calculatedHeight ?? 0 });
			};

			updateHeight();

			const disposable = editor.onDidContentSizeChange(updateHeight);

			return () => disposable.dispose();
		}
	}, [monaco, editor, borderWidth, stableHeight]);

	return <>{children}</>;
};
